import { RadioGroup } from "@headlessui/react";
import { captureException, withScope } from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom } from "jotai";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { FCC } from "types";

import { Sim } from "@/assets/icons/ds";
import { AppearTransition } from "@/components/Interface/AppearTransition/AppearTransition";
import { Button } from "@/components/Interface/Button/Button";
import { FormField } from "@/components/Interface/FormFields/FormField/FormField";
import { RadioButtonOption } from "@/components/Interface/FormFields/RadioButton/RadioButton";
import { Notification } from "@/components/Interface/Notification/Notification";
import { Pill } from "@/components/Interface/Pill/Pill";
import { CardModule } from "@/components/Layout/BaseLayouts/CardModule/CardModule";
import { StepFlowNavigation } from "@/components/Layout/BaseLayouts/StepFlowLayout/StepFlowNavigation";
import { TwoColumnLayout } from "@/components/Layout/BaseLayouts/TwoColumnLayout";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { LightboxWrapper } from "@/components/Layout/Payment/LightboxWrapper/LightboxWrapper";
import { PaymentMeanSection } from "@/components/Layout/Payment/PaymentMeanSection/PaymentMeanSection";
import { SubscriptionInfoBox } from "@/components/Layout/SubscriptionInfoBox/SubscriptionInfoBox";
import { useOptionCheckoutSubmit } from "@/components/Portal/DeviceManagement/hooks/useOptionCheckoutSubmit";
import { useAlert } from "@/hooks/useAlert";
import { useGetCampaignEndDate } from "@/hooks/useGetCampaignEndDate";
import { useHandleCheckoutError } from "@/hooks/useHandleCheckoutError";
import { useHandleError } from "@/hooks/useHandleError";
import { transformPaymentMean } from "@/hooks/usePaymentMode";
import { useQueryCallbacks } from "@/hooks/useQueryCallbacks";
import { ownErrorHandlingMeta } from "@/hooks/useQueryClient";
import { useQueryParam } from "@/hooks/useQueryParam";
import { useSimIdFromQueryParam } from "@/hooks/useSimIdFromQueryParam";
import { FullScreenLoadingContext } from "@/provider/FullScreenLoadingProvider/FullScreenLoadingProvider";
import { portalRoutes } from "@/router/routes/portalRoutes";
import {
  useGetAvailableSunriseRoamingPackages,
  useGetProduct,
  useGetSim,
  useOrderRoamingPackage,
  useUpdateSim,
} from "@/services/api";
import {
  Basket,
  BasketSubscriberOrderType,
  GetAvailableSunriseRoamingPackagesPackageType,
} from "@/services/model";
import {
  AlertTypes,
  keepTypedAlias,
  portalTariffProductIDAtom,
  tariffCheckoutPaymentPersistenceAtom,
} from "@/utils/atoms";
import { getCreditAmount } from "@/utils/basketUtils";
import {
  ChargeTypeIDs,
  SIM_ALIAS_MAX_LENGTH,
  TOTAL_DAYS_IN_YEAR,
} from "@/utils/constants.ts";
import { isValidSimAlias } from "@/utils/dataContextHelpers";
import { getLocalizedDateString } from "@/utils/dateHelpers";
import { getTariffType, isInactive, TariffTypes } from "@/utils/deviceUtils";
import { env } from "@/utils/environmentHelpers";
import { resolveAliasAfterTariffChange } from "@/utils/simAliasDefaults";
import {
  Duration,
  findDurationPeer,
  getNextPaymentDate,
  getSubscriptionRecurringDateOffsetDays,
  TransformedProductsById,
  transformProducts,
} from "@/utils/tariffUtils";
import { getCurrencyFromLookupValue } from "@/utils/translationHelpers";

import { SubscriptionDueNow } from "./SubscriptionDueNow/SubscriptionDueNow";

type FormProps = {
  paymentProvider: string;
  alias: string;
};

export const getVoiceOptionEntryFromRateBasket = (
  rateBasket: Basket | undefined | null,
) => rateBasket?.entries?.[0]?.options?.[0];

export const SubscriptionCheckout: FCC = () => {
  const {
    watch,
    control,
    register,
    setError,
    setValue,
    formState: { errors },
  } = useForm<FormProps>({ mode: "onChange" });
  const [paymentStatus] = useQueryParam("payment");
  const [simId] = useSimIdFromQueryParam();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const handleError = useHandleError();
  const showAlert = useAlert();
  const { setFullScreenLoading } = useContext(FullScreenLoadingContext);
  const { mutateAsync: updateSimAlias } = useUpdateSim();

  const [selectedRateId, setPortalTariffProductID] = useAtom(
    portalTariffProductIDAtom,
  );
  const [tariffPaymentPersistence, setTariffPaymentPersistence] = useAtom(
    tariffCheckoutPaymentPersistenceAtom,
  );

  // Local active rate — can diverge from atom when billing period is switched.
  // Falls back to the persisted product id so the checkout stays functional after a page reload
  // (portalTariffProductIDAtom is not persisted, but tariffCheckoutPaymentPersistenceAtom is).
  const [activeRateId, setActiveRateId] = useState<number | undefined>(
    selectedRateId ?? tariffPaymentPersistence?.product?.id,
  );

  // Keep local state in sync when the atom changes (e.g. user navigates back and re-selects).
  useEffect(() => {
    if (selectedRateId !== undefined) {
      setActiveRateId(selectedRateId);
    }
  }, [selectedRateId]);

  const simQuery = useGetSim(
    { simId: parseInt(simId) },
    {
      query: {
        enabled: !isNaN(parseInt(simId)),
        placeholderData: tariffPaymentPersistence?.simDetail,
      },
    },
  );
  useQueryCallbacks(simQuery, {
    onSuccess: (sim) =>
      setTariffPaymentPersistence((prev) => ({ ...prev, simDetail: sim })),
  });
  const { data: simDetail, isSuccess: hasFetchedSim } = simQuery;

  const orderRoamingPackageQuery = useOrderRoamingPackage(
    Number(simId),
    {
      // @ts-ignore
      optionId: activeRateId,
    },
    {
      query: {
        meta: ownErrorHandlingMeta,
        refetchOnWindowFocus: false,
        enabled: activeRateId !== undefined,
        placeholderData: tariffPaymentPersistence?.subscriberOrderResponse,
      },
    },
  );
  useQueryCallbacks(orderRoamingPackageQuery, {
    onSuccess: (res) =>
      setTariffPaymentPersistence((prev) => ({
        ...prev,
        subscriberOrderResponse: res,
      })),
    onError: (error) => {
      handleError(error);
      const billingPeriodFlag = isBillingPeriodSwitch
        ? "&billingPeriodSwitch=true"
        : "";
      navigate(
        `${portalRoutes.manageDevice.subscription.change}?simId=${simId}${billingPeriodFlag}`,
        { replace: true },
      );
    },
  });
  const {
    data: subscriberOrderResponse,
    isSuccess: hasFetchedOrder,
    isPlaceholderData: isOrderPlaceholder,
    refetch: refetchOrder,
  } = orderRoamingPackageQuery;

  const rateBasket = subscriberOrderResponse?.basket;
  const subscriberOrderType = rateBasket?.subscriberOrderType;
  const discountAmount = rateBasket?.total?.oneTimeCampaign?.amount;
  const credit = getCreditAmount(rateBasket?.balance);
  const totalOnceAmount =
    (rateBasket?.total?.oneTime?.amount || 0) - (discountAmount || 0) - credit;
  const creditTimeRemaining = rateBasket?.total?.oneTime?.amountDiscounts;
  const recurringAmount = rateBasket?.total?.recurring?.amount;

  const sumIsZero = totalOnceAmount <= 0;
  const paymentMode = transformPaymentMean(
    subscriberOrderResponse?.paymentMean,
  );
  const { isInvoice, isCreditCard, maskedCardNumberString, cardType } =
    paymentMode;

  const shouldHidePaymentSelection =
    subscriberOrderType === BasketSubscriberOrderType.DOWNGRADE &&
    (isInvoice || isCreditCard);

  const productQuery = useGetProduct(
    // @ts-ignore
    activeRateId,
    {
      expanded: true,
      client: Number(env.clientNumericId ?? 1),
    },
    {
      query: {
        enabled:
          activeRateId !== undefined && !isNaN(Number(env.clientNumericId)),
        placeholderData: tariffPaymentPersistence?.product,
      },
    },
  );
  useQueryCallbacks(productQuery, {
    onSuccess: (product) =>
      setTariffPaymentPersistence((prev) => ({ ...prev, product })),
  });
  const { data: dataProduct, isSuccess: hasFetchedProduct } = productQuery;

  // Billing period switch — fetch available packages to find monthly/yearly peers.
  const { data: availablePackagesData } = useGetAvailableSunriseRoamingPackages(
    parseInt(simId),
    { packageType: GetAvailableSunriseRoamingPackagesPackageType.Data_CH },
    {
      query: {
        enabled: !isNaN(parseInt(simId)) && activeRateId !== undefined,
        refetchOnWindowFocus: false,
      },
    },
  );

  const processedProductsById = useMemo((): TransformedProductsById => {
    if (!availablePackagesData?.subscriberPackageData) {
      return {};
    }

    return transformProducts(availablePackagesData.subscriberPackageData)
      .processedProductsById;
  }, [availablePackagesData]);

  const currentProduct =
    activeRateId !== undefined
      ? processedProductsById[String(activeRateId)]
      : undefined;

  const monthlyPeer = findDurationPeer(
    currentProduct,
    processedProductsById,
    Duration.month,
  );
  const yearlyPeer = findDurationPeer(
    currentProduct,
    processedProductsById,
    Duration.year,
  );
  const canShowBillingPeriodSwitch =
    !!monthlyPeer && !!yearlyPeer && activeRateId !== undefined;

  const followUpId =
    !isInactive(simDetail) && simDetail?.autoRenew
      ? simDetail.successorForRenew?.id
      : undefined;
  const isMonthlyFollowUp =
    followUpId !== undefined && monthlyPeer?.id === followUpId;
  const isYearlyFollowUp =
    followUpId !== undefined && yearlyPeer?.id === followUpId;

  const currentDuration =
    currentProduct?.duration ??
    (currentProduct?.minContractDur === TOTAL_DAYS_IN_YEAR
      ? Duration.year
      : Duration.month);
  const selectedDuration =
    currentDuration === Duration.year ? Duration.year : Duration.month;

  const [isSwitchingBillingPeriod, setIsSwitchingBillingPeriod] =
    useState(false);
  const pendingBillingPeriodScroll = useRef(false);

  const [billingPeriodSwitchParam] = useQueryParam("billingPeriodSwitch");
  // True when the user arrived via the "Change" shortcut in SubscriptionStatus
  // rather than through the normal rate-selection step.
  const isBillingPeriodSwitch = billingPeriodSwitchParam === "true";

  // Auto-select the opposite billing-period peer once the product catalogue
  // has loaded. The ref prevents the effect from re-firing after the switch
  // updates activeRateId (which would change selectedDuration and re-trigger).
  const hasAutoSwitchedBillingPeriod = useRef(false);
  useEffect(() => {
    if (!isBillingPeriodSwitch || hasAutoSwitchedBillingPeriod.current) {
      return;
    }
    const targetPeer =
      selectedDuration === Duration.month ? yearlyPeer : monthlyPeer;

    if (!targetPeer?.id) {
      return;
    }
    hasAutoSwitchedBillingPeriod.current = true;
    setIsSwitchingBillingPeriod(true);
    pendingBillingPeriodScroll.current = true;
    setActiveRateId(targetPeer.id);
  }, [isBillingPeriodSwitch, selectedDuration, yearlyPeer, monthlyPeer]);

  const scrollToCheckoutTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBillingPeriodChange = async (duration: Duration) => {
    if (duration === selectedDuration || isSwitchingBillingPeriod) {
      return;
    }
    const peer = duration === Duration.year ? yearlyPeer : monthlyPeer;

    if (!peer?.id) {
      return;
    }
    setIsSwitchingBillingPeriod(true);
    setActiveRateId(peer.id);
    scrollToCheckoutTop();
    // isSwitchingBillingPeriod is reset once the order query resolves with the new rate.
  };

  // Reset the switching flag only once the order query returns FRESH data for
  // the new rate. Placeholder data still represents the previous basket — clearing
  // the flag then would allow submitting the wrong basket ID.
  useEffect(() => {
    if (!isOrderPlaceholder) {
      if (pendingBillingPeriodScroll.current) {
        pendingBillingPeriodScroll.current = false;
        scrollToCheckoutTop();
      }
      setIsSwitchingBillingPeriod(false);
    }
  }, [subscriberOrderResponse, isOrderPlaceholder]);

  const tariffType = getTariffType(dataProduct?.id);

  // The name after the change: an untouched default name becomes the new
  // product's default, unless the customer types their own.
  const predictedAlias =
    resolveAliasAfterTariffChange({
      currentAlias: simDetail?.alias,
      newProductId: activeRateId,
      language: i18n.language,
    }) ??
    simDetail?.alias ??
    "";
  const typedAlias =
    tariffPaymentPersistence?.typedAlias?.simId === Number(simId)
      ? tariffPaymentPersistence.typedAlias.value
      : undefined;
  const chosenAlias = (typedAlias ?? predictedAlias).trim();

  // Follows the predicted name (e.g. on a billing period switch) until the
  // customer types; a typed name is restored after the Lightbox redirect.
  useEffect(() => {
    setValue("alias", typedAlias ?? predictedAlias);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [predictedAlias]);

  const saveAlias = async (alias: string) => {
    // The persisted placeholder can still be another SIM's detail - never
    // rename a SIM other than the one in the URL.
    if (simDetail?.simId !== Number(simId)) {
      return false;
    }

    try {
      await updateSimAlias({
        params: { simId: Number(simDetail?.simId) },
        data: { sim_alias: alias },
      });

      return true;
    } catch (err) {
      withScope((scope) => {
        scope.setTag("section", "portal");
        scope.setTag("domain", "SubscriptionCheckout");
        scope.setTag("endpoint", "updateSim");
        scope.setExtra("simId", simDetail?.simId);
        captureException(err);
      });

      return false;
    }
  };

  // A name the customer typed doesn't depend on the tariff, so it is saved
  // before the order: if that fails, they are still here to retry.
  const typedAliasToSave =
    typedAlias !== undefined &&
    isValidSimAlias(chosenAlias) &&
    chosenAlias !== simDetail?.alias?.trim()
      ? chosenAlias
      : null;

  const handleSuccess = async () => {
    setFullScreenLoading(false);
    setPortalTariffProductID(undefined);
    setTariffPaymentPersistence(undefined);

    // An untouched default name follows the new tariff, so it can only be
    // renamed once the change succeeded. That change stays done if the
    // rename fails; the customer can set the name again under Details.
    const defaultAliasToSave =
      typedAlias === undefined && chosenAlias !== simDetail?.alias?.trim()
        ? chosenAlias
        : null;

    if (
      defaultAliasToSave &&
      simDetail?.simId &&
      !(await saveAlias(defaultAliasToSave))
    ) {
      showAlert({
        type: AlertTypes.error,
        text: t("cockpit.managementTile.tariff.checkout.aliasNotSaved", {
          alias: defaultAliasToSave,
        }),
      });
    }

    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["/sim/sim"] }),
      queryClient.invalidateQueries({ queryKey: ["/sim/sims"] }),
    ]);
    setIsSubmittingCheckout(false);
    navigate(
      `${portalRoutes.manageDevice.subscription.display}?simId=${simDetail?.simId}`,
      { replace: true },
    );
  };

  const refreshRateBasket = useCallback(() => {
    void refetchOrder();
  }, [refetchOrder]);
  const {
    submit,
    isSubmittingCheckout,
    setIsSubmittingCheckout,
    datatransRequestDocument,
    isCreatingTransaction,
    isAddingOptionLoading,
    isAddingOptionDone,
  } = useOptionCheckoutSubmit({
    domain: "SubscriptionCheckout",
    simId,
    basket: rateBasket,
    refreshBasket: refreshRateBasket,
    paymentMode,
    onSuccess: handleSuccess,
    skipCardPaymentWhenFree: true,
  });

  const isSuccess = paymentStatus === "success";
  useEffect(() => {
    if (isSuccess && rateBasket?.id && !isSubmittingCheckout) {
      setIsSubmittingCheckout(true);
      handleSuccess();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, handleSuccess]);

  const minContractDurationDays =
    subscriberOrderResponse?.minContractDuration || 30;
  const recurringDateDays = getSubscriptionRecurringDateOffsetDays({
    subscriberOrderType,
    remainingDays: simDetail?.remainingDays,
    minContractDurationDays,
  });
  const recurringDate =
    recurringDateDays !== undefined
      ? t("Common.checkout.dueOn", {
          date: getLocalizedDateString(
            getNextPaymentDate(recurringDateDays),
            i18n.language,
          ),
        })
      : undefined;
  const billingPeriod = t(
    "Onboarding.sections.checkout.summary.billingPeriod",
    { days: minContractDurationDays },
  );
  const recurringBillingPeriod = t(
    "portal:cockpit.managementTile.tariff.payInterval",
    { days: minContractDurationDays },
  );
  const recurringCaption = t(
    "portal:cockpit.managementTile.watchSim.checkout.today",
  );
  const isVoiceOption = dataProduct?.displayValue?.includes("Voice Option");
  const hasWatchSim = !!simDetail?.watchSim;

  const watchedPaymentProvider = watch("paymentProvider");

  useHandleCheckoutError(sumIsZero);

  const handleSubmit = async () => {
    if (isSubmittingCheckout) {
      return;
    }

    if (typedAliasToSave) {
      setFullScreenLoading(true, t("Onboarding.sections.checkout.loading"));
      const isAliasSaved = await saveAlias(typedAliasToSave);
      setFullScreenLoading(false);

      if (!isAliasSaved) {
        setError("alias", {
          type: "server",
          message: t("cockpit.managementTile.tariff.checkout.aliasSaveFailed"),
        });

        return;
      }
    }

    submit(totalOnceAmount);
  };

  const voiceOptionEntry = getVoiceOptionEntryFromRateBasket(rateBasket);

  const voiceOptionOneTimeCharge = useMemo(
    () =>
      voiceOptionEntry?.oneTimeCharges?.filter(
        (value) => value.chargeType?.id === ChargeTypeIDs.VoiceOption,
      ),
    [voiceOptionEntry?.oneTimeCharges],
  );
  const dataProductOneTimeCharge = useMemo(
    () =>
      voiceOptionEntry?.oneTimeCharges?.filter(
        (value) => value.chargeType?.id === 4,
      ),
    [voiceOptionEntry?.oneTimeCharges],
  );
  const voiceOptionCharge = useMemo(
    () =>
      dataProduct?.charges?.filter(
        (charge) => charge.id === voiceOptionOneTimeCharge?.[0]?.charge?.id,
      ),
    [dataProduct, voiceOptionOneTimeCharge],
  );

  const { campaignEndDate } = useGetCampaignEndDate(rateBasket);
  const currency = getCurrencyFromLookupValue(
    rateBasket?.currency || { id: 1 },
  );

  const watchSimPrice = useMemo(
    () =>
      voiceOptionEntry?.options?.[0]?.oneTimeCharges?.find(
        (charge) => charge.chargeType?.id === 4,
      )?.amount,
    [voiceOptionEntry?.options],
  );

  const discountName =
    voiceOptionEntry?.discounts?.[0]?.discountName ??
    t("Onboarding.sections.checkout.summary.discount");

  const disableSuccessButton =
    isCreatingTransaction ||
    isAddingOptionLoading ||
    isAddingOptionDone ||
    isSwitchingBillingPeriod ||
    isOrderPlaceholder ||
    (followUpId !== undefined && activeRateId === followUpId) ||
    (activeRateId === undefined &&
      tariffPaymentPersistence?.product?.id === undefined) ||
    (!watchedPaymentProvider && !(isInvoice || isCreditCard)) ||
    (typedAlias !== undefined && !isValidSimAlias(chosenAlias));

  const hasFetchedEverything =
    hasFetchedSim && hasFetchedOrder && hasFetchedProduct;

  const aboveColumns = (
    // -mt-6 cancels StepFlowLayout's own header-to-content gap (gap-6, see
    // CardContainer in StepFlowLayout.tsx) - per Figma (node 2614:142788)
    // this text sits directly under the stepper, with no gap of its own.
    <div className="-mt-6 flex flex-col gap-4">
      <p className="text-body-sm leading-none text-foreground-secondary">
        <Trans t={t} values={{ count: simDetail?.remainingDays ?? 0 }}>
          {subscriberOrderType === BasketSubscriberOrderType.DOWNGRADE
            ? "cockpit.managementTile.tariff.checkout.tariffActivation.asSuccessor"
            : "cockpit.managementTile.tariff.checkout.tariffActivation.now"}
        </Trans>
      </p>

      {!isVoiceOption &&
        hasWatchSim &&
        tariffType === TariffTypes.dataTariff && (
          <Notification
            tone="warning"
            title={t("cockpit.managementTile.tariff.checkout.watchSimWarning")}
            description={
              <Trans components={[<span key="0" className="font-semibold" />]}>
                cockpit.managementTile.tariff.checkout.watchSimDeactivationHint
              </Trans>
            }
          />
        )}
    </div>
  );

  const summaryLeft = (
    <CardModule
      variant="white"
      bodyGap={6}
      title={t("Common.checkout.summary")}
    >
      <FormField<FormProps>
        id="subscription-checkout-alias"
        name="alias"
        label={t("Common.label.subscriptionName")}
        errors={errors}
        register={register}
        options={{
          maxLength: SIM_ALIAS_MAX_LENGTH,
          validate: (value: string) =>
            isValidSimAlias(value.trim()) ||
            t("Onboarding.sections.checkout.summary.errorDeviceName"),
          onChange: (event) =>
            setTariffPaymentPersistence((prev) => ({
              ...prev,
              typedAlias: { simId: Number(simId), value: event.target.value },
            })),
        }}
      />

      <div className="flex w-full flex-col gap-3">
        <p className="text-body-base font-semibold leading-none text-foreground-primary">
          {t("cockpit.managementTile.tariff.yourTariff")}
        </p>

        <SubscriptionInfoBox className="flex items-center gap-3">
          <Sim className="size-6 shrink-0 text-foreground-primary" />
          <p className="text-body-lg font-semibold leading-md text-foreground-primary">
            {dataProduct?.baseDisplayValue}
            {isVoiceOption &&
              " | " + t("portal:common.device.attributes.voiceOption")}
            {hasWatchSim && " | " + t("Common.products.watchSim")}
          </p>
        </SubscriptionInfoBox>
      </div>
    </CardModule>
  );

  const dueNowRight =
    rateBasket?.currency !== undefined && totalOnceAmount !== undefined ? (
      <SubscriptionDueNow
        totalOnceAmount={totalOnceAmount}
        discountOnceAmount={discountAmount}
        discountName={discountName}
        discountEndDate={campaignEndDate}
        credit={credit}
        creditTimeRemaining={creditTimeRemaining}
        currency={currency}
        billingPeriod={billingPeriod}
        dataRateName={dataProduct?.baseDisplayValue}
        dataRatePrice={dataProductOneTimeCharge?.[0]?.amount}
        voiceOptionPrice={voiceOptionCharge?.[0]?.amount}
        watchSimName={t("Common.products.watchSim")}
        watchSimPrice={watchSimPrice}
        recurringAmount={recurringAmount}
        recurringDate={recurringDate}
        recurringBillingPeriod={recurringBillingPeriod}
        recurringCaption={recurringCaption}
      />
    ) : null;

  const paymentLeft =
    !shouldHidePaymentSelection && rateBasket !== undefined ? (
      <div className="flex w-full flex-col gap-4">
        <PaymentMeanSection
          isInvoice={isInvoice}
          isCreditCard={isCreditCard}
          cardType={cardType}
          maskedCardNumberString={maskedCardNumberString}
          control={control}
          enableShowCurrent={true}
          sumIsZero={sumIsZero}
        />
        <LightboxWrapper
          paymentProvider={watchedPaymentProvider}
          transactionId={datatransRequestDocument?.refNo}
          sign={datatransRequestDocument?.sign}
          enabled={datatransRequestDocument !== undefined}
        />
      </div>
    ) : null;

  const billingPeriodRight =
    !isBillingPeriodSwitch && canShowBillingPeriodSwitch ? (
      <CardModule
        dataTestid="checkout-billing-period-switch"
        variant="white"
        title={t("Onboarding.sections.checkout.billingPeriod.title")}
        subtitle={
          <span className="text-foreground-primary">
            <Trans
              t={t}
              i18nKey="Onboarding.sections.checkout.billingPeriod.description"
              components={{ strong: <strong /> }}
            />
          </span>
        }
      >
        <RadioGroup
          className="flex flex-col gap-2"
          aria-label={t("Onboarding.sections.checkout.billingPeriod.title")}
          value={selectedDuration}
          onChange={(duration: Duration) =>
            void handleBillingPeriodChange(duration)
          }
        >
          <RadioButtonOption
            name="subscription-billing-period"
            value={Duration.month}
            label={t("Onboarding.sections.checkout.billingPeriod.monthly")}
            disabled={isSwitchingBillingPeriod || isMonthlyFollowUp}
          />
          <div className="flex items-center gap-2">
            <RadioButtonOption
              name="subscription-billing-period"
              value={Duration.year}
              label={t("Onboarding.sections.checkout.billingPeriod.yearly")}
              disabled={isSwitchingBillingPeriod || isYearlyFollowUp}
            />
            <Pill
              className="bg-surface-accent-rose text-foreground-primary"
              size="sm"
              text={t(
                "Onboarding.sections.checkout.billingPeriod.freeMonthBadge",
              )}
            />
          </div>
        </RadioGroup>
      </CardModule>
    ) : undefined;

  return (
    <div
      id="checkout-tariff"
      data-testid={subscriberOrderType}
      className="flex w-full flex-col gap-6"
    >
      {!hasFetchedEverything ||
      isCreatingTransaction ||
      isAddingOptionLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner />
        </div>
      ) : (
        <AppearTransition>
          {!activeRateId && !tariffPaymentPersistence?.product?.id ? (
            <p data-testid="no-product-id">
              {t("cockpit.managementTile.tariff.checkout.noProductId")}
            </p>
          ) : (
            <div id="checkout-layout" className="flex flex-col gap-6">
              {aboveColumns}
              <TwoColumnLayout
                independentColumns
                rows={[
                  { left: summaryLeft, right: dueNowRight },
                  { left: paymentLeft, right: billingPeriodRight },
                ]}
              />
            </div>
          )}
        </AppearTransition>
      )}

      <StepFlowNavigation
        primaryButton={
          <Button
            dataTestid="success-button"
            variant="primary"
            disabled={disableSuccessButton}
            onClick={() => void handleSubmit()}
          >
            {t("common.buttons.submit")}
          </Button>
        }
        secondaryButton={
          <Button
            variant="secondary"
            onClick={() => {
              setTariffPaymentPersistence(keepTypedAlias);
              navigate(
                isBillingPeriodSwitch
                  ? `${portalRoutes.manageDevice.subscription.display}?simId=${simId}`
                  : `${portalRoutes.manageDevice.subscription.change}?simId=${simId}`,
                { replace: true },
              );
            }}
          >
            {t("Common.label.back")}
          </Button>
        }
      />
    </div>
  );
};
